import { useState, useEffect, useRef } from "react";
import dropDownArrow from "../../assets/img/arrow-down.svg";
import Search from "../../assets/img/search-normal.svg";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import Text from "../Text";

const Label = styled("div")(({ theme }) => ({
  fontSize: 12,
  color: theme.color.label,
  fontWeight: 700,
  letterSpacing: 0.32,
  textAlign: "start",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
}));

const DropdownContainer = styled("div")(({ theme, isMobile }) => ({
  background: "#F7F5FF",
  border: `1px solid ${theme.color.primary}`,
  borderRadius: 25,
  width: isMobile ? "auto" : 384
}));

const DropdownInput = styled("div")(({ theme, isMobile }) => ({
  padding: `0 20px`,
  fontSize: 14,
  color: theme.color.navy,
  fontWeight: 400,
  letterSpacing: 0.32,
  textAlign: "start",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  userSelect: "none",
  lineHeight: 1,
  height: isMobile ? 26 : 40
}));

const DropDownItem = styled("div")(({ theme, selected, isMobile }) => ({
  cursor: "pointer",
  backgroundColor: selected ? theme.color.background : "unset",
  height: "40px",
  lineHeight: "36px",
  textIndent: isMobile ? "0" : "16px",

  ":hover": {
    backgroundColor: theme.color.gray10,
    borderRadius: 20
  },
}));

const DropdownTool = styled("div")``;
const DropdownSelectedValue = styled("div")`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const DropdownTools = styled("div")``;

const OptionsContainer = styled("div")(({ theme }) => ({
  maxHeight: 260,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    backgroundColor: theme.color.background,
    width: 2,
  },

  "&::-webkit-scrollbar-thumb": {
    height: "1%",
    width: 2,
    backgroundColor: theme.color.label,
  },
}));

const Menu = styled("div")(({ theme, isMobile }) => ({
  position: "absolute",
  transform: "translateY(15px)",
  overflow: "auto",
  backgroundColor: "#fff",
  padding: isMobile ? 20 : 16,
  color: theme.color.text,
  zIndex: 99,
  boxShadow: `0px 4px 20px #C3C0D3`,
  borderRadius: 18,
  top: 37,
  width: "100%"
}));

const SelectLabel = styled("div")(({ theme, disabled }) => ({
  fontSize: 12,
  color: theme.color.label,
  fontWeight: 600,
  letterSpacing: 0.32,
  textAlign: "start",
  opacity: disabled ? 0.4 : 1,
}));

const Icon = () => {
  return (
    <img
      alt="dropDownArrow"
      src={dropDownArrow}
      style={{ marginInlineStart: 12 }}
    />
  );
};
const SearchIcon = () => {
  return <img alt="SearchIcon" src={Search} />;
};

const SearchInput = styled("input")(() => ({
  boxSizing: "border-box",
  border: "none",
  width: "90%",
}));

const SearchBox = styled("div")(() => ({
  width: "100%",
  padding: 10,
  borderWidth: "1px",
  border: "none",
  borderBottom: "1px solid #ccc",
  alignItems: "baseline",
  flexDirection: "row",
  justifyContent: "space-between",
  display: "flex",
  marginBottom: "20px",
}));

const DropDownMenu = ({
  value,
  placeHolder,
  options,
  selectLabel,
  onSelect,
  disabled,
  valid,
  style,
  isSearchable = false,
  isMobile
}) => {
  const [selectedValue, setSelectedValue] = useState(value);
  const [showMenu, setShowMenu] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const inputRef = useRef();
  const searchRef = useRef();
  const { t } = useTranslation("common");
  const theme = useTheme();

  useEffect(() => {
    if (options && options.length === 1) {
      setSelectedValue(options[0]);
      onSelect(options[0].value);
    } else {
      setSelectedValue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    if (!!value?.value) {
      setSelectedValue(value);
    } else {
      setSelectedValue(null);
    }
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };

    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
    };
  }, []);

  useEffect(() => {
    setSearchValue("");
    if (showMenu && searchRef.current) {
      searchRef.current.focus();
    }
  }, [showMenu]);

  const onSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleInputclick = () => {
    setShowMenu(!showMenu);
  };

  const getDisplay = () => {
    if (!!selectedValue?.label) {
      return selectedValue.label;
    }
    return placeHolder;
  };

  const onItemClick = (option) => {
    if (option === selectedValue) {
      setSelectedValue(null);
      onSelect("");
    } else {
      setSelectedValue(option);
      onSelect(option.value);
      setShowMenu(false);
    }
  };

  const isSelected = (option) => {
    if (!selectedValue) {
      return false;
    }
    return selectedValue.value === option.value;
  };

  const renderOptions = () => {
    const result = !!searchValue
      ? options?.filter(
        (option) =>
          option.label?.toLowerCase().indexOf(searchValue.toLowerCase()) >= 0
      )
      : options;
    if (result.length > 0) {
      return result?.map((option, index) => {
        return (
          <DropDownItem
            key={index}
            onClick={() => onItemClick(option)}
            selected={isSelected(option)}
            isMobile={isMobile}
          >
            <Text.SM color={theme.color.navy} style={{
              textAlign: "start",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block"
            }}>
              {option.label}
            </Text.SM>
          </DropDownItem >
        );
      });
    } else {
      return <Label> {t("no_result_found")}</Label>;
    }
  };

  return (
    <div style={{ ...style, width: style?.width, position: "relative" }}>
      <SelectLabel disabled={disabled}>{selectLabel}</SelectLabel>
      <DropdownContainer disabled={disabled} valid={valid} isMobile={isMobile}>
        <DropdownInput
          ref={inputRef}
          onClick={handleInputclick}
          isMobile={isMobile}
        >
          <DropdownSelectedValue>{getDisplay()}</DropdownSelectedValue>
          <DropdownTools>
            <DropdownTool>
              <Icon />
            </DropdownTool>
          </DropdownTools>
        </DropdownInput>
        {showMenu && (
          <Menu isMobile={isMobile}>
            {isSearchable && (
              <SearchBox>
                <SearchIcon />
                <SearchInput
                  onChange={onSearch}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  value={searchValue}
                  ref={searchRef}
                />
              </SearchBox>
            )}
            <OptionsContainer>{renderOptions()}</OptionsContainer>
          </Menu>
        )}
      </DropdownContainer>
    </div>
  );
};

export default DropDownMenu;
